import { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Button,
	Card,
	DatePicker,
	Empty,
	Input,
	Segmented,
	Select,
	Space,
	Spin,
	Typography,
} from 'antd';
import { PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link, useSearchParams } from 'react-router-dom';

import Header from '../../../../components/Header';
import MaterialHistoryDrawer from '../../../../components/MaterialMovements/MaterialHistoryDrawer';
import {
	ACTION_META,
	ACTION_ORDER,
	SHOP_TIMEZONE,
} from '../../../../components/MaterialMovements/movementMeta';
import { useGetBuildingsQuery } from '../../../../redux/api/location';
import {
	MovementAction,
	MovementReportBar,
	useGetMaterialMovementReportQuery,
} from '../../../../redux/api/material-movements';
import { useGetUsersQuery } from '../../../../redux/api/users';
import { useLocationLabel } from '../../../../utils/locationLabel';
import { buildAuditLines } from './auditLines';
import BarGroups from './BarGroups';
import {
	MovementFilters,
	PRESETS,
	PRESET_LABELS,
	Preset,
	filtersFromSearchParams,
	filtersToSearchParams,
	formatRange,
	presetRange,
	toReportParams,
} from './filters';
import OperatorBreakdown from './OperatorBreakdown';
import PrintableMovementReport from './PrintableMovementReport';
import SummaryCards from './SummaryCards';
import './print.css';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const PRINT_MODE_CLASS = 'movement-print-mode';
/** Injected rather than in print.css: `@page` cannot be scoped to one page. */
const PRINT_PAGE_RULE = '@page { size: letter landscape; margin: 0.4in; }';

/**
 * Daily spot-check report: every logged material movement in a window,
 * grouped by bar so an auditor can walk the racks and tick each one off.
 */
const MaterialMovements = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const filters = useMemo(
		() => filtersFromSearchParams(searchParams),
		[searchParams]
	);
	const [barDraft, setBarDraft] = useState(filters.bar);
	const [historyBar, setHistoryBar] = useState<MovementReportBar | null>(null);

	useEffect(() => setBarDraft(filters.bar), [filters.bar]);

	const apply = (patch: Partial<MovementFilters>) =>
		setSearchParams(filtersToSearchParams({ ...filters, ...patch }), {
			replace: true,
		});

	const { data, currentData, isFetching, isError, error, refetch } =
		useGetMaterialMovementReportQuery(toReportParams(filters));
	// `data` keeps the previous range on screen while the next one loads; only
	// `currentData` matches the filters, so it alone may be printed.
	const ready = Boolean(currentData) && !isError;

	const formatLocation = useLocationLabel();
	const showDate = Boolean(data && data.date_from !== data.date_to);
	const sections = useMemo(
		() => (data ? buildAuditLines(data.groups, { showDate, formatLocation }) : []),
		[data, showDate, formatLocation]
	);

	const { data: users } = useGetUsersQuery({ ordering: 'first_name' });
	const { data: buildings } = useGetBuildingsQuery();
	const buildingOptions = useMemo(
		() => (buildings ?? []).map((b) => ({ value: b.id, label: b.name })),
		[buildings]
	);

	const operatorOptions = useMemo(() => {
		const byId = new Map<number, string>();
		users?.results.forEach((user) =>
			byId.set(
				Number(user.id),
				user.full_name || `${user.first_name} ${user.last_name}`.trim() || user.email
			)
		);
		data?.operators.forEach((op) => {
			if (op.id !== null && !byId.has(op.id)) byId.set(op.id, op.name);
		});
		return [...byId.entries()]
			.map(([value, label]) => ({ value, label }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [users, data]);

	// Print mode only once a report exists, so Ctrl+P during loading or after
	// an error does not print a blank sheet.
	useEffect(() => {
		if (!ready) return;
		const style = document.createElement('style');
		style.media = 'print';
		style.textContent = PRINT_PAGE_RULE;
		document.head.appendChild(style);
		document.body.classList.add(PRINT_MODE_CLASS);
		return () => {
			style.remove();
			document.body.classList.remove(PRINT_MODE_CLASS);
		};
	}, [ready]);

	const toggleAction = (action: MovementAction) =>
		apply({
			actions:
				filters.actions.length === 1 && filters.actions[0] === action
					? []
					: [action],
		});

	const errorDetail =
		(error as { data?: { detail?: string } } | undefined)?.data?.detail ??
		'The report could not be loaded.';
	const hasFilters =
		filters.performedBy.length > 0 ||
		filters.actions.length > 0 ||
		Boolean(filters.bar) ||
		filters.buildings.length > 0;

	return (
		<>
			<Header
				title="Material Movements"
				breadcrumbItems={[
					{ title: <Link to="/">Home</Link> },
					{ title: 'Reports' },
					{ title: 'Material Movements' },
				]}
			/>

			<Space direction="vertical" size="middle" style={{ width: '100%' }}>
				<Card size="small" className="shadow-sm">
					<div className="flex flex-wrap items-center justify-between gap-3">
						<Space wrap>
							<Segmented
								data-testid="movement-presets"
								value={filters.preset}
								options={PRESETS.map((value) => ({
									value,
									label: PRESET_LABELS[value],
								}))}
								onChange={(value) => {
									const preset = value as Preset;
									apply(
										preset === 'custom'
											? { preset }
											: { preset, ...presetRange(preset) }
									);
								}}
							/>
							{filters.preset === 'custom' && (
								<RangePicker
									allowClear={false}
									value={[dayjs(filters.dateFrom), dayjs(filters.dateTo)]}
									disabledDate={(current) =>
										Boolean(
											current &&
												current.isAfter(dayjs().tz(SHOP_TIMEZONE), 'day')
										)
									}
									onChange={(range) => {
										if (!range?.[0] || !range?.[1]) return;
										apply({
											preset: 'custom',
											dateFrom: range[0].format('YYYY-MM-DD'),
											dateTo: range[1].format('YYYY-MM-DD'),
										});
									}}
								/>
							)}
							<Text type="secondary">
								{formatRange(filters.dateFrom, filters.dateTo)} · Central time
							</Text>
						</Space>
						<Space wrap>
							<Button
								icon={<PrinterOutlined />}
								disabled={!ready || isFetching}
								onClick={() => window.print()}
							>
								Print
							</Button>
							<Button
								icon={<ReloadOutlined />}
								loading={isFetching}
								onClick={() => refetch()}
							>
								Refresh
							</Button>
						</Space>
					</div>

					<div className="mt-3 flex flex-wrap gap-2">
						<Select
							mode="multiple"
							allowClear
							showSearch
							optionFilterProp="label"
							placeholder="All operators"
							style={{ minWidth: 220 }}
							maxTagCount="responsive"
							value={filters.performedBy}
							options={operatorOptions}
							onChange={(performedBy: number[]) => apply({ performedBy })}
						/>
						<Select
							mode="multiple"
							allowClear
							placeholder="All actions"
							style={{ minWidth: 220 }}
							maxTagCount="responsive"
							value={filters.actions}
							options={ACTION_ORDER.map((value) => ({
								value,
								label: ACTION_META[value].label,
							}))}
							onChange={(actions: MovementAction[]) => apply({ actions })}
						/>
						<Select
							mode="multiple"
							allowClear
							placeholder="All buildings"
							style={{ minWidth: 180 }}
							maxTagCount="responsive"
							value={filters.buildings}
							options={buildingOptions}
							onChange={(next: string[]) => apply({ buildings: next })}
						/>
						<Input.Search
							allowClear
							placeholder="Bar ID (e.g. AAB)"
							style={{ width: 200 }}
							value={barDraft}
							onChange={(event) => setBarDraft(event.target.value)}
							onSearch={(value) => apply({ bar: value.trim().toUpperCase() })}
						/>
						{hasFilters && (
							<Button
								onClick={() =>
									apply({ performedBy: [], actions: [], bar: '', buildings: [] })
								}
							>
								Clear filters
							</Button>
						)}
					</div>
				</Card>

				{isError && (
					<Alert
						type="error"
						showIcon
						message={errorDetail}
						action={<Button onClick={() => refetch()}>Retry</Button>}
					/>
				)}

				{!data && isFetching && (
					<div className="flex justify-center py-12">
						<Spin />
					</div>
				)}

				{data && !isError && (
					<Spin spinning={isFetching}>
						<Space direction="vertical" size="middle" style={{ width: '100%' }}>
							<div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
								<Title level={5} style={{ margin: 0 }}>
									{data.summary.transactions} movements ·{' '}
									{data.summary.bars_touched} bars ·{' '}
									{data.summary.operators} operators
								</Title>
								<Text type="secondary" className="text-xs">
									Click a card to show only that action.
								</Text>
							</div>

							<SummaryCards
								summary={data.summary}
								selected={filters.actions}
								onToggle={toggleAction}
							/>

							{data.truncated && (
								<Alert
									type="warning"
									showIcon
									message={`Showing the first ${data.row_cap.toLocaleString()} movements.`}
									description="The counts above cover everything, but the list is cut off. Narrow the dates or add a filter to see the rest."
								/>
							)}

							{data.operators.length > 0 && (
								<Card size="small" className="shadow-sm" title="By operator">
									<OperatorBreakdown
										operators={data.operators}
										onSelect={(id) => apply({ performedBy: [id] })}
									/>
								</Card>
							)}

							{data.groups.length ? (
								<BarGroups
									sections={sections}
									showDate={showDate}
									formatLocation={formatLocation}
									onOpenHistory={setHistoryBar}
								/>
							) : (
								<Card className="shadow-sm">
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description={
											hasFilters
												? 'No movements match these filters.'
												: 'No material movements were logged in this range.'
										}
									/>
								</Card>
							)}

							<Text type="secondary" className="text-xs">
								Only movements made through the app are logged. Bars moved by hand
								without updating the system will not appear here, which is what
								the spot check is for. Rows tagged “historical” were rebuilt from
								older records and may be missing the from-location or length.
							</Text>
						</Space>
					</Spin>
				)}
			</Space>

			{ready && currentData && (
				<PrintableMovementReport
					data={currentData}
					sections={sections}
					filters={filters}
					operatorOptions={operatorOptions}
					buildingOptions={buildingOptions}
				/>
			)}

			<MaterialHistoryDrawer
				materialLengthId={historyBar?.id ?? null}
				friendlyId={historyBar?.friendly_id}
				open={Boolean(historyBar)}
				onClose={() => setHistoryBar(null)}
			/>
		</>
	);
};

export default MaterialMovements;
